<template>
  <div class="page-container">
    <header class="header">
      <el-button text class="back-btn" @click="handleBack" aria-label="返回">
        <img src="@/assets/返回.svg" alt="返回" class="back-icon" />
      </el-button>
      <div class="header-title">库存库位查询</div>
      <div class="header-right"></div>
    </header>

    <main class="content">
      <div class="tab-bar">
        <button
          class="tab-item"
          :class="{ active: activeTab === 'warehouse' }"
          @click="activeTab = 'warehouse'"
        >
          库位查询
        </button>
        <button
          class="tab-item"
          :class="{ active: activeTab === 'tray' }"
          @click="activeTab = 'tray'"
        >
          托盘查询
        </button>
      </div>

      <!-- 库位查询 tab -->
      <template v-if="activeTab === 'warehouse'">
        <div class="search-card">
          <div class="form-row">
            <div class="label required">供应商</div>
            <el-select
              v-model="selectedSupplierId"
              class="supplier-select"
              placeholder="请输入供应商名称搜索"
              filterable
              remote
              :remote-method="handleSupplierInput"
              :loading="supplierLoading"
              clearable
              @change="handleSupplierChange"
              @clear="clearSupplier"
            >
              <el-option
                v-for="s in supplierOptions"
                :key="s.id"
                :label="s.supplierName"
                :value="s.id"
              />
            </el-select>
          </div>

          <div class="form-row">
            <div class="label">物料编码</div>
            <el-input
              v-model="materialCodeKeyword"
              placeholder="请输入物料编码（支持模糊搜索）"
              clearable
            />
          </div>

          <el-button
            type="primary"
            round
            class="search-btn"
            :disabled="!canSearch"
            :loading="loading && searched"
            @click="handleSearch"
          >
            查询
          </el-button>
        </div>

        <div v-if="statusMsg" class="status-banner" :class="statusType">
          {{ statusMsg }}
        </div>

        <div
          class="result-area"
          v-loading="loading && !searched"
          element-loading-text="加载中..."
        >
          <div v-if="!searched" class="empty-state">
            <el-empty description="请输入物料编码进行查询" :image-size="80" />
          </div>

          <div v-else-if="list.length === 0 && !loading" class="empty-state">
            <el-empty description="暂无数据" :image-size="80" />
          </div>

          <div v-else class="result-list">
            <div
              class="material-card"
              v-for="group in list"
              :key="group.materialNo"
            >
              <div class="material-header">
                <div class="material-no">{{ group.materialNo }}</div>
                <div class="material-desc">{{ group.materialDesc }}</div>
              </div>
              <div class="stock-table">
                <div
                  class="stock-row"
                  v-for="(item, index) in group.stockList"
                  :key="index"
                >
                  <div class="stock-cell warehouse-name-cell">
                    <span class="warehouse-name">{{
                      item.wareName || "-"
                    }}</span>
                    <el-tag
                      :type="item.isBad === 1 ? 'danger' : 'success'"
                      size="small"
                      round
                    >
                      {{ item.isBad === 1 ? "不良品" : "良品" }}
                    </el-tag>
                  </div>
                  <div class="stock-cell stock-num-cell">
                    <span class="stock-label">库存数量：</span>
                    <span class="stock-value">{{ item.stockNum || "0" }}</span>
                  </div>
                </div>
              </div>
            </div>

            <div class="list-footer">
              <div class="page-info">
                已加载 {{ list.length }} / {{ total }}
              </div>
              <el-button
                type="primary"
                text
                @click="loadMore"
                :loading="loadingMore"
                :disabled="finished"
              >
                {{
                  finished
                    ? "没有更多了"
                    : loadingMore
                    ? "加载中..."
                    : "加载更多"
                }}
              </el-button>
            </div>
          </div>
        </div>
      </template>

      <!-- 托盘查询 tab -->
      <template v-if="activeTab === 'tray'">
        <div class="search-card">
          <div class="form-row">
            <div class="label required">托盘码</div>
            <div class="input-wrap">
              <el-input
                v-model="trayKeyword"
                placeholder="请输入/扫描托盘码"
                clearable
                @input="handleTrayInput"
              >
                <template #suffix>
                  <el-button
                    type="text"
                    class="scan-btn"
                    @click="startTrayScan"
                    aria-label="扫码"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      stroke-width="2"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      width="20"
                      height="20"
                    >
                      <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
                      <path
                        d="M7 7h3v3H7zM14 7h3v3h-3zM7 14h3v3H7zM14 14h3v3h-3z"
                      />
                    </svg>
                  </el-button>
                </template>
              </el-input>
              <div v-if="trayDropdownOpen" class="inline-dropdown">
                <div v-if="trayLoading" class="dropdown-loading">加载中...</div>
                <div
                  v-else-if="trayOptions.length === 0"
                  class="dropdown-empty"
                >
                  暂无数据
                </div>
                <button
                  v-else
                  v-for="p in trayOptions"
                  :key="p.id"
                  type="button"
                  class="dropdown-option"
                  @click="confirmTray(p)"
                >
                  <div class="option-main">{{ p.palletCode || "-" }}</div>
                  <div class="option-sub">
                    {{ p.storeName ? p.storeName : "" }}
                    {{ p.wareAreaName ? ` | ${p.wareAreaName}` : "" }}
                    {{ p.wareName ? ` | ${p.wareName}` : "" }}
                  </div>
                </button>
              </div>
            </div>
          </div>

          <div class="form-row">
            <div class="label">供应商</div>
            <el-select
              v-model="traySelectedSupplierId"
              class="supplier-select"
              placeholder="请输入供应商名称搜索"
              filterable
              remote
              :remote-method="handleTraySupplierInput"
              :loading="traySupplierLoading"
              clearable
              @change="handleTraySupplierChange"
              @clear="clearTraySupplier"
            >
              <el-option
                v-for="s in traySupplierOptions"
                :key="s.id"
                :label="s.supplierName"
                :value="s.id"
              />
            </el-select>
          </div>

          <div class="form-row">
            <div class="label">物料编码</div>
            <div class="input-wrap">
              <el-input
                v-model="trayMaterialCodeKeyword"
                placeholder="请输入物料编码"
                clearable
                @input="handleTrayMaterialCodeInput"
              />
              <div v-if="trayMaterialCodeDropdownOpen" class="inline-dropdown">
                <div v-if="trayMaterialCodeLoading" class="dropdown-loading">
                  加载中...
                </div>
                <div
                  v-else-if="trayMaterialCodeOptions.length === 0"
                  class="dropdown-empty"
                >
                  暂无数据
                </div>
                <button
                  v-else
                  v-for="m in trayMaterialCodeOptions"
                  :key="m.id"
                  type="button"
                  class="dropdown-option"
                  @click="confirmTrayMaterialCode(m)"
                >
                  <div class="option-main">{{ m.materialNo || "-" }}</div>
                  <div class="option-sub">{{ m.supplierName || "" }}</div>
                </button>
              </div>
            </div>
          </div>

          <el-button
            type="primary"
            round
            class="search-btn"
            :disabled="!trayCanSearch"
            :loading="trayLoading_ && traySearched"
            @click="handleTraySearch"
          >
            查询
          </el-button>
        </div>

        <div v-if="trayStatusMsg" class="status-banner" :class="trayStatusType">
          {{ trayStatusMsg }}
        </div>

        <div
          class="result-area"
          v-loading="trayLoading_ && !traySearched"
          element-loading-text="加载中..."
        >
          <div v-if="!traySearched" class="empty-state">
            <el-empty description="请输入托盘码进行查询" :image-size="80" />
          </div>

          <div
            v-else-if="trayResultList.length === 0 && !trayLoading_"
            class="empty-state"
          >
            <el-empty description="暂无数据" :image-size="80" />
          </div>

          <div v-else class="result-list">
            <div
              class="tray-supplier-group"
              v-for="(sg, sIdx) in trayGroupedResult"
              :key="sg.supplierName + '_' + sIdx"
            >
              <div class="tray-supplier-title">
                <span class="tray-supplier-dot"></span>
                <span>{{ sg.supplierName || "未知供应商" }}</span>
              </div>
              <div
                class="tray-material-card"
                v-for="(group, gIdx) in sg.materials"
                :key="group.materialNo + '_' + gIdx"
              >
                <div
                  class="tray-card-header"
                  @click="toggleTrayExpand(sIdx + '_' + gIdx)"
                >
                  <div class="tray-card-mid">
                    <div class="tray-material-no">
                      <span class="tray-seq">{{
                        trayMaterialIndex(sIdx, gIdx)
                      }}</span>
                      {{ group.materialNo }}
                    </div>
                    <div v-if="group.materialDesc" class="tray-material-desc">
                      {{ group.materialDesc }}
                    </div>
                  </div>
                  <div class="tray-card-right">
                    <div class="tray-card-stock">
                      <div class="tray-stock-item">
                        <span class="tray-stock-dot good"></span>
                        <span class="tray-stock-num good">{{
                          formatNum(group.goodNum)
                        }}</span>
                      </div>
                      <div class="tray-stock-item">
                        <span class="tray-stock-dot bad"></span>
                        <span class="tray-stock-num bad">{{
                          formatNum(group.badNum)
                        }}</span>
                      </div>
                    </div>
                    <span
                      class="tray-expand-icon"
                      :class="{ expanded: trayExpandedMap[sIdx + '_' + gIdx] }"
                      >▼</span
                    >
                  </div>
                </div>
                <div
                  v-if="trayExpandedMap[sIdx + '_' + gIdx]"
                  class="tray-card-body"
                >
                  <div
                    v-if="!group.stockList || group.stockList.length === 0"
                    class="tray-empty-detail"
                  >
                    暂无批次明细
                  </div>
                  <div v-else class="tray-batch-list">
                    <div
                      class="tray-batch-item"
                      v-for="(batch, bIdx) in group.stockList"
                      :key="bIdx"
                    >
                      <div class="tray-batch-top">
                        <span class="tray-batch-no">{{
                          batch.batchNo || "-"
                        }}</span>
                        <el-tag
                          :type="batch.isBad === 1 ? 'danger' : 'success'"
                          size="small"
                          round
                        >
                          {{ batch.isBad === 1 ? "不良品" : "良品" }}
                        </el-tag>
                      </div>
                      <div class="tray-batch-bottom">
                        <span class="tray-batch-qty">
                          数量：<b>{{ formatNum(batch.stockNum) }}</b>
                        </span>
                        <span
                          v-if="batch.stockAge !== ''"
                          class="tray-batch-age"
                        >
                          库龄：{{ batch.stockAge }}天
                        </span>
                        <span v-if="batch.createTime" class="tray-batch-time">
                          {{ batch.createTime }}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div v-if="trayTotalPages > 1" class="tray-pagination">
              <button
                class="tray-page-btn"
                :disabled="trayCurrent <= 1"
                @click="trayGoPage(trayCurrent - 1)"
              >
                上一页
              </button>
              <span class="tray-page-info"
                >{{ trayCurrent }} / {{ trayTotalPages }}</span
              >
              <button
                class="tray-page-btn"
                :disabled="trayCurrent >= trayTotalPages"
                @click="trayGoPage(trayCurrent + 1)"
              >
                下一页
              </button>
            </div>
          </div>
        </div>
      </template>
    </main>
    <!-- 扫码组件 -->
    <CameraScan
      v-model:visible="trayScanVisible"
      title="扫描托盘码"
      @success="handleTrayScanSuccess"
      @close="trayScanVisible = false"
    />
  </div>
</template>

<script>
import { ref, reactive, computed, onMounted } from "vue";
import { useRouter } from "vue-router";
import { getStockWareDetail } from "@/api/stockQuery";
import { getHfSupplierPage } from "@/api/hfSupplier";
import {
  getPalletDetail,
  getPalletCodeList,
  searchMaterialByNo,
} from "@/api/stockTransfer";
import CameraScan from "@/components/CameraScan";

export default {
  name: "StockWareQuery",
  components: {
    CameraScan,
  },
  setup() {
    const router = useRouter();

    const handleBack = () => router.push("/home");

    // ========== Tab ==========
    const activeTab = ref("warehouse");

    // ========== 库位查询 ==========
    const materialCodeKeyword = ref("");

    const supplierKeyword = ref("");
    const supplierLoading = ref(false);
    const supplierOptions = ref([]);
    const supplierSearchSeq = ref(0);
    const selectedSupplier = ref(null);
    const selectedSupplierId = ref("");

    const current = ref(1);
    const size = ref(20);
    const list = ref([]);
    const total = ref(0);
    const loading = ref(false);
    const loadingMore = ref(false);
    const searched = ref(false);

    const statusMsg = ref("");
    const statusType = ref("info");

    const finished = computed(
      () => list.value.length >= total.value && total.value > 0,
    );

    const canSearch = computed(() => {
      return selectedSupplier.value;
    });

    const mergeGroupedList = (existing, newItems) => {
      const map = new Map();
      existing.forEach((item) => {
        map.set(item.materialNo, { ...item, stockList: [...item.stockList] });
      });
      newItems.forEach((item) => {
        if (map.has(item.materialNo)) {
          const existingItem = map.get(item.materialNo);
          existingItem.stockList.push(...item.stockList);
        } else {
          map.set(item.materialNo, { ...item });
        }
      });
      return Array.from(map.values());
    };

    const handleSupplierInput = async (keyword) => {
      supplierKeyword.value = keyword;
      const seq = supplierSearchSeq.value + 1;
      supplierSearchSeq.value = seq;

      if (!keyword || !keyword.trim()) {
        supplierOptions.value = [];
        return;
      }

      supplierLoading.value = true;
      try {
        const res = await getHfSupplierPage({ keyword: keyword.trim() });
        if (seq !== supplierSearchSeq.value) return;
        const data = res?.data || {};
        const records = Array.isArray(data?.records) ? data.records : [];
        supplierOptions.value = records;
      } catch (e) {
        if (seq !== supplierSearchSeq.value) return;
        supplierOptions.value = [];
      } finally {
        if (seq === supplierSearchSeq.value) {
          supplierLoading.value = false;
        }
      }
    };

    const handleSupplierChange = (val) => {
      if (val) {
        const s = supplierOptions.value.find((item) => item.id === val);
        selectedSupplier.value = s || null;
        supplierKeyword.value = s?.supplierName || "";
      } else {
        selectedSupplier.value = null;
        supplierKeyword.value = "";
      }
    };

    const clearSupplier = () => {
      selectedSupplier.value = null;
      selectedSupplierId.value = "";
      supplierKeyword.value = "";
      supplierOptions.value = [];
    };

    const fetchData = async ({ reset } = { reset: false }) => {
      if (reset) {
        current.value = 1;
        list.value = [];
        total.value = 0;
      }

      loading.value = reset;
      loadingMore.value = !reset;
      statusMsg.value = "";

      try {
        const res = await getStockWareDetail({
          materialNo: materialCodeKeyword.value.trim(),
          supplierId: selectedSupplier.value?.id,
          current: current.value,
          size: size.value,
        });
        const records = res?.data?.records || [];
        const groupedList = [];
        records.forEach((item) => {
          const stockDetailList = item?.materialStockDetailList || [];
          if (!stockDetailList || stockDetailList.length === 0) {
            return;
          }
          const materialNo = item?.materialNo || "未知编码";
          const materialDesc = item?.materialDesc || "";
          const supplierName = item?.supplierName || "";
          const stockList = stockDetailList.map((stock) => ({
            wareName: stock.wareName || stock.batchNo || "-",
            stockNum: stock.stockNum || "0",
            isBad: stock.isBad || 0,
          }));
          groupedList.push({
            materialNo,
            materialDesc,
            supplierName,
            stockList,
          });
        });
        const nextList = reset
          ? groupedList
          : mergeGroupedList(list.value, groupedList);
        list.value = nextList;
        total.value = res?.data?.total || nextList.length;
        searched.value = true;
      } catch (e) {
        statusMsg.value = e?.message || "查询失败";
        statusType.value = "error";
      } finally {
        loading.value = false;
        loadingMore.value = false;
      }
    };

    const handleSearch = () => {
      fetchData({ reset: true });
    };

    const loadMore = async () => {
      if (finished.value || loading.value || loadingMore.value) return;
      current.value += 1;
      await fetchData({ reset: false });
    };

    // ========== 托盘查询 ==========
    const trayKeyword = ref("");
    const trayDropdownOpen = ref(false);
    const trayLoading = ref(false);
    const trayOptions = ref([]);
    const traySearchSeq = ref(0);
    const trayScanVisible = ref(false);

    const traySelectedSupplier = ref(null);
    const traySelectedSupplierId = ref("");
    const traySupplierKeyword = ref("");
    const traySupplierLoading = ref(false);
    const traySupplierOptions = ref([]);
    const traySupplierSearchSeq = ref(0);

    const trayMaterialCodeKeyword = ref("");
    const trayMaterialCodeDropdownOpen = ref(false);
    const trayMaterialCodeLoading = ref(false);
    const trayMaterialCodeOptions = ref([]);
    const trayMaterialCodeSearchSeq = ref(0);
    const traySelectedMaterialId = ref("");

    const trayCurrent = ref(1);
    const traySize = ref(20);
    const trayResultList = ref([]);
    const trayTotal = ref(0);
    const trayLoading_ = ref(false);
    const traySearched = ref(false);

    const trayStatusMsg = ref("");
    const trayStatusType = ref("info");

    const trayExpandedMap = reactive({});

    const trayGroupedResult = computed(() => {
      const groups = new Map();
      trayResultList.value.forEach((item) => {
        const key = item.supplierName || "未知供应商";
        if (!groups.has(key)) {
          groups.set(key, { supplierName: key, materials: [] });
        }
        groups.get(key).materials.push(item);
      });
      return Array.from(groups.values());
    });

    const trayMaterialIndex = (sIdx, gIdx) => {
      let idx = 0;
      for (let i = 0; i < sIdx; i++) {
        idx += trayGroupedResult.value[i].materials.length;
      }
      return idx + gIdx + 1;
    };

    const trayTotalPages = computed(() => {
      return Math.ceil(trayTotal.value / traySize.value) || 1;
    });

    const trayCanSearch = computed(() => {
      return !!trayKeyword.value.trim();
    });

    const toggleTrayExpand = (idx) => {
      trayExpandedMap[idx] = !trayExpandedMap[idx];
    };

    const handleTrayInput = async () => {
      const keyword = (trayKeyword.value || "").trim();
      trayDropdownOpen.value = !!keyword;

      const seq = traySearchSeq.value + 1;
      traySearchSeq.value = seq;

      if (!keyword) {
        trayOptions.value = [];
        return;
      }

      trayLoading.value = true;
      try {
        const res = await getPalletCodeList({ palletCode: keyword });
        if (seq !== traySearchSeq.value) return;
        const data = res?.data;
        trayOptions.value = Array.isArray(data) ? data : [];
      } catch (e) {
        if (seq !== traySearchSeq.value) return;
        trayOptions.value = [];
      } finally {
        if (seq === traySearchSeq.value) {
          trayLoading.value = false;
        }
      }
    };

    const confirmTray = (p) => {
      trayKeyword.value = p?.palletCode || "";
      trayDropdownOpen.value = false;
    };

    const startTrayScan = () => {
      trayScanVisible.value = true;
    };

    const handleTrayScanSuccess = (decodedText) => {
      const text = String(decodedText || "")
        .replace(/[\u200B-\u200D\uFEFF]/g, "")
        .replace(/[\r\n\t]/g, "")
        .trim();

      if (!text) return;

      trayKeyword.value = text;
      trayScanVisible.value = false;
    };

    const handleTraySupplierInput = async (keyword) => {
      traySupplierKeyword.value = keyword;
      const seq = traySupplierSearchSeq.value + 1;
      traySupplierSearchSeq.value = seq;

      if (!keyword || !keyword.trim()) {
        traySupplierOptions.value = [];
        return;
      }

      traySupplierLoading.value = true;
      try {
        const res = await getHfSupplierPage({ keyword: keyword.trim() });
        if (seq !== traySupplierSearchSeq.value) return;
        const data = res?.data || {};
        const records = Array.isArray(data?.records) ? data.records : [];
        traySupplierOptions.value = records;
      } catch (e) {
        if (seq !== traySupplierSearchSeq.value) return;
        traySupplierOptions.value = [];
      } finally {
        if (seq === traySupplierSearchSeq.value) {
          traySupplierLoading.value = false;
        }
      }
    };

    const handleTraySupplierChange = (val) => {
      if (val) {
        const s = traySupplierOptions.value.find((item) => item.id === val);
        traySelectedSupplier.value = s || null;
        traySupplierKeyword.value = s?.supplierName || "";
      } else {
        traySelectedSupplier.value = null;
        traySupplierKeyword.value = "";
      }
    };

    const clearTraySupplier = () => {
      traySelectedSupplier.value = null;
      traySelectedSupplierId.value = "";
      traySupplierKeyword.value = "";
      traySupplierOptions.value = [];
    };

    const handleTrayMaterialCodeInput = async () => {
      const keyword = (trayMaterialCodeKeyword.value || "").trim();
      trayMaterialCodeDropdownOpen.value = !!keyword;

      const seq = trayMaterialCodeSearchSeq.value + 1;
      trayMaterialCodeSearchSeq.value = seq;

      if (!keyword) {
        trayMaterialCodeOptions.value = [];
        traySelectedMaterialId.value = "";
        return;
      }

      trayMaterialCodeLoading.value = true;
      try {
        const searchParams = { materialNo: keyword };
        if (traySelectedSupplier.value?.id) {
          searchParams.supplierId = traySelectedSupplier.value.id;
        }
        const res = await searchMaterialByNo(searchParams);
        if (seq !== trayMaterialCodeSearchSeq.value) return;
        const data = res?.data || {};
        const records = Array.isArray(data?.records) ? data.records : [];
        trayMaterialCodeOptions.value = records;
        if (records.length === 1) {
          confirmTrayMaterialCode(records[0]);
        }
      } catch (e) {
        if (seq !== trayMaterialCodeSearchSeq.value) return;
        trayMaterialCodeOptions.value = [];
      } finally {
        if (seq === trayMaterialCodeSearchSeq.value) {
          trayMaterialCodeLoading.value = false;
        }
      }
    };

    const confirmTrayMaterialCode = (m) => {
      if (m) {
        traySelectedMaterialId.value = m.id;
        trayMaterialCodeKeyword.value = m.materialNo || "";
      } else {
        traySelectedMaterialId.value = "";
        trayMaterialCodeKeyword.value = "";
      }
      trayMaterialCodeDropdownOpen.value = false;
    };

    const calcStockAge = (createTime) => {
      if (!createTime) return "";
      const created = new Date(createTime);
      if (isNaN(created.getTime())) return "";
      const now = new Date();
      const diffMs = now - created;
      const days = Math.floor(diffMs / (1000 * 60 * 60 * 24));
      return days;
    };

    const formatNum = (val) => {
      const n = Number(val);
      if (!Number.isFinite(n)) return val || "0";
      if (Number.isInteger(n)) return String(n);
      return String(parseFloat(n.toFixed(2)));
    };

    const fetchTrayData = async () => {
      trayLoading_.value = true;
      trayStatusMsg.value = "";
      Object.keys(trayExpandedMap).forEach((k) => delete trayExpandedMap[k]);

      try {
        const params = {
          palletCode: trayKeyword.value.trim(),
          current: trayCurrent.value,
          size: traySize.value,
        };
        if (traySelectedSupplier.value?.id) {
          params.supplierId = traySelectedSupplier.value.id;
        }
        if (traySelectedMaterialId.value) {
          params.materialAndId = traySelectedMaterialId.value;
        }

        const res = await getPalletDetail(params);
        const data = res?.data || {};
        let records = [];
        if (Array.isArray(data)) {
          records = data;
        } else if (Array.isArray(data.records)) {
          records = data.records;
        }

        let allMaterials = [];
        records.forEach((record) => {
          if (Array.isArray(record.materialList)) {
            allMaterials = allMaterials.concat(record.materialList);
          }
        });

        const grouped = allMaterials.map((m) => {
          const stockRows = Array.isArray(m.stockList) ? m.stockList : [];
          let goodNum = 0;
          let badNum = 0;
          const batchList = [];
          stockRows.forEach((s) => {
            const qty = Number(s.stockNum) || 0;
            if ((s.isBad ?? m.isBad) === 1) {
              badNum += qty;
            } else {
              goodNum += qty;
            }
            batchList.push({
              batchNo: s.batchNo || "",
              stockNum: s.stockNum || "0",
              isBad: (s.isBad ?? m.isBad) === 1 ? 1 : 0,
              createTime: s.createTime || "",
              stockAge: calcStockAge(s.createTime),
            });
          });
          return {
            materialNo: m.materialNo || "",
            materialDesc: m.materialDesc || "",
            supplierName: m.supplierName || "",
            goodNum,
            badNum,
            stockList: batchList,
          };
        });

        trayResultList.value = grouped;
        trayTotal.value = data.total || 0;
        traySearched.value = true;
      } catch (e) {
        trayStatusMsg.value = e?.message || "查询失败";
        trayStatusType.value = "error";
      } finally {
        trayLoading_.value = false;
      }
    };

    const handleTraySearch = () => {
      trayCurrent.value = 1;
      trayResultList.value = [];
      trayTotal.value = 0;
      fetchTrayData();
    };

    const trayGoPage = (page) => {
      if (page < 1 || page > trayTotalPages.value) return;
      trayCurrent.value = page;
      fetchTrayData();
    };

    onMounted(() => {
      window.scrollTo({ top: 0, behavior: "smooth" });
    });

    return {
      activeTab,

      // 库位查询
      materialCodeKeyword,
      supplierKeyword,
      supplierLoading,
      supplierOptions,
      selectedSupplier,
      selectedSupplierId,
      list,
      total,
      loading,
      loadingMore,
      finished,
      searched,
      statusMsg,
      statusType,
      canSearch,
      handleBack,
      handleSupplierInput,
      handleSupplierChange,
      clearSupplier,
      handleSearch,
      loadMore,

      // 托盘查询
      trayKeyword,
      trayDropdownOpen,
      trayLoading,
      trayOptions,
      handleTrayInput,
      confirmTray,
      trayScanVisible,
      startTrayScan,
      handleTrayScanSuccess,
      traySelectedSupplier,
      traySelectedSupplierId,
      traySupplierKeyword,
      traySupplierLoading,
      traySupplierOptions,
      handleTraySupplierInput,
      handleTraySupplierChange,
      clearTraySupplier,
      trayMaterialCodeKeyword,
      trayMaterialCodeDropdownOpen,
      trayMaterialCodeLoading,
      trayMaterialCodeOptions,
      traySelectedMaterialId,
      handleTrayMaterialCodeInput,
      confirmTrayMaterialCode,
      trayResultList,
      trayTotal,
      trayLoading_,
      traySearched,
      trayStatusMsg,
      trayStatusType,
      trayCanSearch,
      trayExpandedMap,
      trayGroupedResult,
      trayCurrent,
      trayTotalPages,
      toggleTrayExpand,
      handleTraySearch,
      trayGoPage,
      trayMaterialIndex,
      formatNum,
    };
  },
};
</script>

<style scoped>
.page-container {
  min-height: 100vh;
  background: var(--background-color);
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica,
    Arial, sans-serif;
}

.page-container,
.page-container * {
  box-sizing: border-box;
}

.header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 14px;
  padding-top: calc(10px + env(safe-area-inset-top));
  background-color: var(--header-background);
  color: var(--header-text);
  box-shadow: 0 2px 8px var(--shadow-light);
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 1000;
  height: calc(52px + env(safe-area-inset-top));
  box-sizing: border-box;
}

.back-btn {
  width: 36px;
  height: 36px;
}

.back-icon {
  width: 20px;
  height: 20px;
}

.header-title {
  font-size: 16px;
  font-weight: 600;
  flex: 1;
  text-align: center;
}

.header-right {
  width: 36px;
}

.content {
  padding: 8px 10px;
  margin-top: calc(52px + env(safe-area-inset-top));
}

/* ========== Tab Bar ========== */
.tab-bar {
  display: flex;
  background: var(--card-background);
  border-radius: 10px;
  padding: 3px;
  margin-bottom: 10px;
  border: 1px solid var(--border-color);
}

.tab-item {
  flex: 1;
  padding: 9px 0;
  border: none;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 500;
  color: var(--text-secondary);
  background: transparent;
  cursor: pointer;
  transition: all 0.25s ease;
}

.tab-item.active {
  background: var(--primary-color);
  color: white;
  font-weight: 600;
  box-shadow: 0 2px 8px rgba(35, 177, 192, 0.3);
}

/* ========== Search Card ========== */
.search-card {
  background: var(--card-background);
  border-radius: 10px;
  padding: 10px;
  box-shadow: 0 2px 8px var(--shadow-light);
  border: 1px solid var(--border-color);
  margin-bottom: 8px;
}

.form-row {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 10px;
}

.label {
  flex: 0 0 64px;
  font-size: 13px;
  color: var(--text-secondary);
}

.label.required::after {
  content: "*";
  color: #ef4444;
  margin-left: 2px;
}

.supplier-select {
  flex: 1;
}

.supplier-select :deep(.el-select__wrapper) {
  height: 38px;
  border-radius: 8px;
}

.input-wrap {
  flex: 1;
  position: relative;
}

.input-wrap :deep(.el-input__suffix) {
  pointer-events: auto;
}

.input-wrap .scan-btn {
  padding: 0;
  margin-right: 4px;
  color: var(--primary-color);
}

.input-wrap .scan-btn:hover {
  color: var(--primary-color);
}

.search-btn {
  width: 100%;
  height: 40px;
  margin-top: 4px;
  --el-button-bg-color: #5bc0de !important;
  --el-button-border-color: #5bc0de !important;
}

/* ========== Result Area ========== */
.result-area {
  min-height: 100px;
}

.status-banner {
  margin-bottom: 8px;
  padding: 8px 10px;
  border-radius: 8px;
  font-size: 12px;
}

.status-banner.error {
  background: rgba(239, 68, 68, 0.12);
  color: #ef4444;
}

.empty-state {
  padding: 30px 0;
  text-align: center;
}

.result-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.material-card {
  background: var(--card-background);
  border-radius: 10px;
  border: 1px solid var(--border-color);
  overflow: hidden;
}

.material-header {
  display: flex;
  flex-direction: column;
  padding: 10px;
  background: rgba(35, 177, 192, 0.06);
  border-bottom: 1px solid var(--border-color);
}

.material-no {
  font-size: 14px;
  font-weight: 600;
  color: var(--text-primary);
}

.material-desc {
  font-size: 11px;
  color: var(--text-secondary);
  margin-top: 2px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.stock-table {
  padding: 0 10px;
}

.stock-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 0;
  border-bottom: 1px solid var(--border-color);
}

.stock-row:last-child {
  border-bottom: none;
}

.stock-cell {
  display: flex;
  align-items: center;
}

.warehouse-name-cell {
  gap: 6px;
}

.stock-num-cell {
  gap: 4px;
}

.warehouse-name {
  font-size: 13px;
  color: var(--text-primary);
}

.stock-label {
  font-size: 12px;
  color: var(--text-secondary);
}

.stock-value {
  font-size: 13px;
  font-weight: 600;
  color: var(--primary-color);
}

.list-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 6px 0;
}

.page-info {
  color: var(--text-secondary);
  font-size: 11px;
}

/* ========== Inline Dropdown ========== */
.inline-dropdown {
  position: absolute;
  z-index: 20;
  left: 0;
  right: 0;
  top: 40px;
  background: var(--card-background);
  border-radius: 8px;
  padding: 4px;
  border: 1px solid var(--border-color);
  max-height: 200px;
  overflow-y: auto;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.dropdown-loading,
.dropdown-empty {
  padding: 12px;
  text-align: center;
  color: var(--text-secondary);
  font-size: 13px;
}

.dropdown-option {
  width: 100%;
  padding: 8px 10px;
  border: none;
  background: transparent;
  text-align: left;
  border-bottom: 1px solid var(--border-color);
  cursor: pointer;
  border-radius: 4px;
}

.dropdown-option:last-child {
  border-bottom: none;
}

.dropdown-option:hover {
  background: var(--background-color);
}

.option-main {
  font-size: 13px;
  font-weight: 500;
  color: var(--text-primary);
}

.option-sub {
  font-size: 11px;
  color: var(--text-secondary);
  margin-top: 2px;
}

/* ========== Tray Card ========== */
.tray-supplier-group {
  margin-bottom: 8px;
}

.tray-supplier-title {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 6px 10px;
  font-size: 12px;
  font-weight: 600;
  color: var(--text-secondary);
}

.tray-supplier-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: var(--primary-color);
  flex-shrink: 0;
}

.tray-material-card {
  background: var(--card-background);
  border-radius: 10px;
  border: 1px solid var(--border-color);
  overflow: hidden;
}

.tray-card-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 12px;
  background: rgba(35, 177, 192, 0.04);
  cursor: pointer;
  user-select: none;
}

.tray-card-mid {
  flex: 1;
  min-width: 0;
  text-align: left;
}

.tray-material-no {
  font-size: 14px;
  font-weight: 700;
  color: var(--text-primary);
  text-align: left;
  display: flex;
  align-items: center;
  gap: 6px;
}

.tray-seq {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background: var(--primary-color);
  color: white;
  font-size: 11px;
  font-weight: 600;
  flex-shrink: 0;
}

.tray-material-desc {
  font-size: 11px;
  color: var(--text-secondary);
  margin-top: 2px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  text-align: left;
}

.tray-card-right {
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  gap: 8px;
  margin-left: 8px;
}

.tray-card-stock {
  display: flex;
  align-items: center;
  gap: 10px;
}

.tray-stock-item {
  display: flex;
  align-items: center;
  gap: 3px;
}

.tray-stock-dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
}

.tray-stock-dot.good {
  background: #10b981;
}

.tray-stock-dot.bad {
  background: #ef4444;
}

.tray-stock-num {
  font-size: 13px;
  font-weight: 700;
}

.tray-stock-num.good {
  color: #10b981;
}

.tray-stock-num.bad {
  color: #ef4444;
}

.tray-expand-icon {
  font-size: 11px;
  color: var(--text-secondary);
  transition: transform 0.25s ease;
}

.tray-expand-icon.expanded {
  transform: rotate(180deg);
}

.tray-card-body {
  border-top: 1px solid var(--border-color);
  padding: 8px 10px;
  background: var(--card-background);
}

.tray-empty-detail {
  padding: 10px 0;
  text-align: center;
  color: var(--text-secondary);
  font-size: 12px;
}

.tray-batch-list {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.tray-batch-item {
  padding: 8px 10px;
  background: rgba(35, 177, 192, 0.03);
  border-radius: 6px;
  border: 1px solid var(--border-color);
}

.tray-batch-top {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
}

.tray-batch-no {
  font-size: 13px;
  font-weight: 600;
  color: var(--text-primary);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.tray-batch-bottom {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: 4px;
}

.tray-batch-qty {
  font-size: 12px;
  color: var(--text-secondary);
}

.tray-batch-qty b {
  color: var(--primary-color);
  font-weight: 700;
}

.tray-batch-time {
  font-size: 11px;
  color: var(--text-secondary);
}

.tray-batch-age {
  font-size: 11px;
  color: var(--primary-color);
  font-weight: 500;
}

/* ========== Tray Pagination ========== */
.tray-pagination {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  padding: 12px 0;
}

.tray-page-btn {
  padding: 8px 16px;
  border: 1px solid var(--border-color);
  border-radius: 8px;
  background: var(--card-background);
  color: var(--text-primary);
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
}

.tray-page-btn:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}

.tray-page-btn:not(:disabled):active {
  background: var(--primary-color);
  color: white;
  border-color: var(--primary-color);
}

.tray-page-info {
  font-size: 13px;
  color: var(--text-secondary);
  min-width: 50px;
  text-align: center;
}
</style>
